import { ComponentRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CompiereDataFieldType } from '@compiere-ws/models/compiere-data-json';
import SpecificWindowUiComponent from '@iupics-components/specific/window/specific-window-ui/specific-window-ui.component';
import AdditionalInfoUiComponent from '@iupics-components/standard/layouts/additional-info-ui/additional-info-ui.component';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import { CacheManagerService } from '@iupics-manager/managers/cache-manager/cache-manager.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { AbstractDataContainer } from '@iupics-manager/models/abstract-datacontainer';
import { AbstractDynamicComponent } from '@iupics-manager/models/abstract-dynamic-component';
import { DynamicComponent } from '@iupics-manager/models/dynamic-component';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsField } from '@iupics-manager/models/iupics-data';
import { IupicsEvent, IupicsTypeEvent } from '@iupics-manager/models/iupics-event';
import { createComponent } from '@iupics-util/tools/component-cache-loader';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { cloneDeep } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class WindowFactoryService {
  #uiCreatorService = inject(UICreatorService);

  newEventHandler(event: IupicsEvent): void {
    switch (event.type) {
      case IupicsTypeEvent.showGridView:
        this.#uiCreatorService.getTab(event.item.windowId, event.item.tabId).subscribe((tabUI) => {
          // const tabUI: ApizTabUI = event.data;
          event.item.component = 'GridViewUiComponent';
          event.item.data = tabUI.editTabs[0].data;
          this.addContainerComponent(event.item, event.item.isCssOnComponent);
          event.item.DOMParentComponent.onChildUpdate(event);
        });
        break;
      case IupicsTypeEvent.showEditView:
      case IupicsTypeEvent.showAdditionalInfo:
        this.#uiCreatorService.getTab(event.item.windowId, event.item.tabId).subscribe((tabUI) => {
          let viewSize = tabUI.editTabs[0].data.editData.nbCol;
          let tabsTransformed = [];
          let tabsTransformedAdditional = [];
          if (event.type === IupicsTypeEvent.showEditView) {
            tabsTransformed = tabUI.editTabs;
            tabsTransformedAdditional = tabUI.additionalTabs;
            if (tabsTransformed.length === 0) {
              tabsTransformed = tabsTransformedAdditional;
              tabsTransformedAdditional = [];
            }
          } else {
            viewSize = tabUI.additionalTabs[0].data.editData.nbCol;
            tabsTransformed = tabUI.additionalTabs;
          }

          event.item.children = [...tabsTransformed];

          const compRef = this.addContainerComponent(event.item, event.item.isCssOnComponent);
          if (compRef) {
            if (compRef.instance instanceof EditViewUiComponent) {
              const { instance } = compRef;
              if (tabsTransformedAdditional.length > 0) {
                viewSize = 1;
                instance.additionalInfoWidthExpanded = tabUI.additionalTabs[0].data.editData.nbCol;
              }

              instance.linkedComponents = event.item.linkedComponents;
              instance.isReadOnly = tabUI.editTabs[0] ? tabUI.editTabs[0].data.isReadOnly : false;
              instance.isDeleteable = tabUI.editTabs[0] ? tabUI.editTabs[0].data.isDeletable : false;
              instance.isInsertRecord = tabUI.editTabs[0] ? tabUI.editTabs[0].data.isInsertRecord : false;
              instance.table_id = tabUI.editTabs[0] ? tabUI.editTabs[0].data.AD_Table_ID : 0;
              instance.data = tabUI.editTabs[0] ? tabUI.editTabs[0].data : null;
            }

            // avoid resize of grid on additionnalInfoShow
            if (event.type !== IupicsTypeEvent.showAdditionalInfo) {
              (compRef.instance as AbstractDynamicComponent).notifierLinkedComponent.next({
                type: IupicsTypeEvent.collapseEvent,
                item: tabUI.editTabs[0],
              });
            }
          }

          event.item.DOMParentComponent.onChildUpdate(event);
          if (compRef.instance instanceof EditViewUiComponent) {
            event.item.container.updateContainerZone(viewSize); // TODO_WR: revoir la logique avec du css
          }
          event.item.DOMParentComponent.onChildUpdate({
            type: IupicsTypeEvent.addBreadcrumbItem,
            item: {
              container: null,
              tabId: tabUI.editTabs[0].tabId,
              data: {
                label: tabUI.editTabs[0].data.label,
              },
            },
          });
        });
        break;
      case IupicsTypeEvent.removeChildEvent:
        this.removeComponent(event.item);
        event.item.DOMParentComponent.onChildUpdate(event);
        event.item.container.updateContainerZone(0);
        break;
      case IupicsTypeEvent.showProductAttributeView:
        this.transformADFieldtoIupics(
          cloneDeep(CacheManagerService.productAttributes_caching.get(event.item.productAttributeId)),
          1
        );
        break;
      case IupicsTypeEvent.showProcess:
        event.item.children = cloneDeep(CacheManagerService.process_Params_caching.get(event.item.tabId)?.children);
        if (event.item.children) {
          for (const child of event.item.children) {
            if (((child.cssClass as string) ?? '').indexOf('p-col-12') === -1) {
              child.cssClass = ((child.cssClass ?? '') + ' p-col-12').trim();
            }
          }
        }
        this.addContainerComponent(event.item, event.item.isCssOnComponent);
        break;
      case IupicsTypeEvent.showSpecificWindow:
        this.#uiCreatorService.getSpecificWindow(event.item.tabId).subscribe((specificWindow) => {
          (event.item.container as SpecificWindowUiComponent).buildWindow(specificWindow);
        });
        break;
      default:
        break;
    }
  }

  private addContainerComponent<T>(item: DynamicComponent, isCssOnComponent = true): ComponentRef<T> {
    if (item.DOMParentComponent.vcr) {
      Global.startPerf(item.DOMParentComponent, 'addContainerComponent.' + item.component);
      let componentRef: ComponentRef<T>;
      if (
        item.component === 'AdditionalInfoUiComponent' &&
        (item.DOMParentComponent as EditViewUiComponent).additionalInfoVcr
      ) {
        componentRef = createComponent(
          (item.DOMParentComponent as EditViewUiComponent).additionalInfoVcr,
          item.component as string
        );
        (item.DOMParentComponent as EditViewUiComponent).additionalInfoComponent =
          componentRef.instance as AdditionalInfoUiComponent;
      } else {
        // componentRef = item.DOMParentComponent.vcr.createComponent(componentName);
        componentRef = createComponent(item.DOMParentComponent.vcr, item.component as string);
      }

      if (item.component === 'GridViewUiComponent' || item.component === 'EditTabUiComponent') {
        (componentRef.instance as AbstractDynamicComponent).isReadOnly = item.data.isReadOnly;
        (componentRef.instance as AbstractDynamicComponent).isInsertRecord = item.data.isInsertRecord;
      }

      if (!item.container) {
        item.container = item.DOMParentComponent.container;
      }

      switch ((item.container as AbstractDynamicComponent).windowType) {
        case IupicsMenuType.WINDOW:
          (componentRef.instance as AbstractDataContainer).fieldType = CompiereDataFieldType.FIELD;
          break;
        case IupicsMenuType.PROCESS:
          (componentRef.instance as AbstractDataContainer).fieldType = CompiereDataFieldType.PROCESS_PARA;
          break;
        case IupicsMenuType.FORM:
          (componentRef.instance as AbstractDataContainer).fieldType = CompiereDataFieldType.FORM_ITEM;
          break;
      }

      (componentRef.instance as AbstractDynamicComponent).container = item.container;
      (componentRef.instance as AbstractDynamicComponent).DOMParentComponent =
        item.DOMParentComponent instanceof AdditionalInfoUiComponent
          ? item.DOMParentComponent.DOMParentComponent
          : item.DOMParentComponent;
      (componentRef.instance as AbstractDynamicComponent).DOMComponent = componentRef;
      (componentRef.instance as AbstractDynamicComponent).data = item.data;
      (componentRef.instance as AbstractDynamicComponent).gridPaginator = item.data?.gridPaginator;
      if (item.tabId) {
        (componentRef.instance as AbstractDynamicComponent).tabId = item.tabId;
      }

      if (item.data?.gridTabFilter) {
        (componentRef.instance as AbstractDynamicComponent).gridTabFilter = item.data?.gridTabFilter;
      }
      if (item.initGridParams) {
        (componentRef.instance as AbstractDynamicComponent).initGridParams = item.initGridParams;
      }
      if (item.data?.gridTabValidator) {
        (componentRef.instance as AbstractDynamicComponent).gridTabValidator = item.data?.gridTabValidator;
      }

      if (item.zoomInfo) {
        (componentRef.instance as AbstractDynamicComponent).zoomInfo = item.zoomInfo;
      }
      if (item.parentStore) {
        (componentRef.instance as AbstractDynamicComponent).parentStore = item.parentStore;
      }
      if (item.parentTab || (item.DOMParentComponent && item.DOMParentComponent.parentTab)) {
        (componentRef.instance as AbstractDynamicComponent).parentTab = item.parentTab
          ? item.parentTab
          : item.DOMParentComponent.parentTab;
      }
      if (item.zoomTarget) {
        (componentRef.instance as AbstractDynamicComponent).zoomTarget = item.zoomTarget;
      }
      if (item.parentProcess || (item.DOMParentComponent && item.DOMParentComponent.parentProcess)) {
        (componentRef.instance as AbstractDynamicComponent).parentProcess = item.parentProcess
          ? item.parentProcess
          : item.DOMParentComponent.parentProcess;
        (componentRef.instance as AbstractDataContainer).fieldType = CompiereDataFieldType.PROCESS_PARA;
      }
      if (item.zoomTargetData) {
        (componentRef.instance as AbstractDynamicComponent).zoomTargetData = item.zoomTargetData;
      }
      if (item.linkedComponents) {
        for (const element of item.linkedComponents) {
          (componentRef.instance as AbstractDynamicComponent).addSubscribeOnLinkedComponent(element, componentRef);
        }
      }
      if (isCssOnComponent) {
        if (componentRef.location?.nativeElement) {
          componentRef.location.nativeElement.className = item.cssClass;
        }
      } else {
        (componentRef.instance as AbstractDynamicComponent).cssClass = item.cssClass;
      }
      (componentRef.instance as AbstractDynamicComponent).componentEmitter
        .pipe(takeUntilDestroyed((componentRef.instance as AbstractDynamicComponent).destroyRef))
        .subscribe((event) => {
          this.newEventHandler(event);
        });

      /*additional info */
      item.DOMParentComponent.DOMChildrenComponent.push(componentRef.instance as AbstractDynamicComponent);
      if (item.children) {
        (componentRef.instance as AbstractDynamicComponent).children = item.children.filter(
          (child) => child !== undefined
        );
        for (const child of (componentRef.instance as AbstractDynamicComponent).children) {
          child.DOMParentComponent = componentRef.instance as AbstractDynamicComponent;
        }
      }
      Global.endPerf(item.DOMParentComponent, 'addContainerComponent.' + item.component);
      return componentRef;
    } else {
      return undefined;
    }
  }

  private removeComponent(item: DynamicComponent): void {
    const componentToRemoved = item.component as AbstractDynamicComponent;

    const vcrIndex: number = item.DOMParentComponent.vcr.indexOf(componentToRemoved.DOMComponent.hostView);
    const compIndex = item.DOMParentComponent.DOMChildrenComponent.indexOf(componentToRemoved);

    if (vcrIndex === -1) {
      return;
    }

    let length = item.DOMParentComponent.DOMChildrenComponent.length;
    for (let i = compIndex; i < length; i++) {
      item.DOMParentComponent.DOMChildrenComponent[compIndex].onRemoveComponent({
        type: IupicsTypeEvent.removeChildEvent,
        item: {
          container: null,
          tabId: item.DOMParentComponent.DOMChildrenComponent[compIndex].tabId,
          data: {
            label: '',
          },
        },
      });
      item.DOMParentComponent.DOMChildrenComponent.splice(compIndex, 1);
      /*Mise à jour de l'url en cas de suppression d'un élément*/
      if (item.container) {
        item.container.notifyUrlChange();
      }
    }

    length = item.DOMParentComponent.vcr.length;
    for (let i = length - 1; i >= vcrIndex; i--) {
      item.DOMParentComponent.vcr.remove(i);
    }
  }

  private transformADFieldtoIupics(children: IupicsField[], nbCol: number): IupicsField[] {
    let currentRow = 0;
    let newRow = true;
    const fields: IupicsField[] = [];
    let fieldsCol: number[] = [];
    for (let i = 0; i < nbCol; i++) {
      fieldsCol.push(0);
    }
    let className: string;
    for (const child of children) {
      if (child.component === 'ProgressBarUiComponent' || child.component === 'GridViewUiComponent') {
        return children;
      } else {
        if (child.component === 'AccordionUiComponent') {
          child.children = this.transformADFieldtoIupics(child.children, nbCol);
        }

        const currentPos = child.data.nbCol;
        const currentColspan = child.data.colspan;
        if (!newRow) {
          for (let j = currentPos - 1; j < fieldsCol.length; j++) {
            if (fieldsCol[j]) {
              newRow = true;
            }
          }
        }

        if (newRow) {
          fieldsCol = [];
          for (let m = 0; m < nbCol; m++) {
            fieldsCol.push(0);
          }
          let countOffest = 0;
          let finish = false;
          let k = currentPos - 2;
          while (k > -1 && !finish) {
            if (!fieldsCol[k--]) {
              countOffest++;
            } else {
              finish = true;
            }
          }
          currentRow++;
          className = 'p-col-' + (12 / nbCol) * currentColspan + ' p-offset-' + countOffest * (12 / nbCol);
          if (child && child.isRange) {
            className += ' rangeFrom';
          }
          child.cssClass = className;
          fields[currentRow] = {
            cssClass: '',
            component: 'RowUiComponent',
            children: [child],
          };
          newRow = false;
        } else {
          let countOffest = 0;
          let finish = false;
          let k = currentPos - 2;
          while (k > -1 && !finish) {
            if (!fieldsCol[k--]) {
              countOffest++;
            } else {
              finish = true;
            }
          }
          child.cssClass = 'p-col-' + (12 / nbCol) * currentColspan + ' p-offset-' + countOffest * (12 / nbCol);
          if (child.data && child.data.fromRange) {
            child.cssClass += ' rangeTo';
          }
          fields[currentRow].children.push(child);
        }
        for (let n = currentPos - 1; n < currentPos - 1 + currentColspan; n++) {
          fieldsCol[n] = 1;
        }
      }
    }
    return fields;
  }
}
